import {
    CaseReducer,
    createAsyncThunk,
    createSlice,
    PayloadAction,
} from '@reduxjs/toolkit';
import {RootState} from './store';
import {PatientPrescriptionHistoryDto} from '../types/prescription';
import PrescriptionsApi from '../api/prescriptions/prescriptionsApi';
import {AppointmentDetailsForPrescriptionDto} from '../types/appointments';
import {P1Response, P1ResponseWithPagination} from '../types/p1Service';
import PatientDataAccessApi from '../api/patientDataAccess/patientDataAccessApi';
import { DateTime } from 'luxon';
import {PatientDataAccessStatusDto} from '../types/patientDataAccess';

const sliceName = 'patientPrescriptionHistory';
interface PrescriptionHistoryState {
    authCode: string;
    codeSent: boolean;
    applicationId: string;
    prescriptionHistory: PatientPrescriptionHistoryDto[];
    page: number;
    size: number;
    count: number;
    fromDateTime: Date | null;
    errorText: string;
    applicationDateTime: string | null;
    authorizationDateTime: string | null;
    status: 'idle' | 'loading' | 'failed';
}

const initialState: PrescriptionHistoryState = {
    authCode: '',
    codeSent: false,
    applicationId: '',
    prescriptionHistory: null,
    page: 0,
    size: 5,
    count: 0,
    fromDateTime: DateTime.now().minus({ years: 1 }).toJSDate(),
    errorText: '',
    applicationDateTime: null,
    authorizationDateTime: null,
    status: 'idle',
};

export const fetchPatientDataAccessStatus = createAsyncThunk<
    P1Response<PatientDataAccessStatusDto>,
    AppointmentDetailsForPrescriptionDto,
    {state: RootState}
>(
    `${sliceName}/fetchPatientDataAccessStatus`,
    async ({medic, patient, id}) => {
        const {data} = await PatientDataAccessApi.getPatientDataAccessStatus({
            appointmentId: id,
            patientId: patient.userId,
            medicId: medic.userId,
            patientPesel: patient.pesel,
            medicNPWZ: medic.npwz,
            medicBusinessRole: medic.businessRole,
        });
        return data;
    },
);

export const fetchPrescriptionHistory = createAsyncThunk<
    P1ResponseWithPagination<PatientPrescriptionHistoryDto>,
    AppointmentDetailsForPrescriptionDto,
    {state: RootState}
>(
    `${sliceName}/fetchPrescriptionHistory`,
    async ({medic, patient, id}, thunkAPI) => {
        const {page, size, fromDateTime} = thunkAPI.getState().patientPrescriptionHistory;
        const {data} = await PrescriptionsApi.getPatientPrescriptionHistory({
            appointmentId: id,
            medicId: medic.userId,
            medicNPWZ: medic.npwz,
            medicBusinessRole: medic.businessRole,
            patientId: patient.userId,
            patientPesel: patient.pesel,
            fromDateTime,
            page,
            size,
        });
        if (data.isSuccess) {
            return data;
        }
        return null;
    },
);

export const authorizePatientDataAccess = createAsyncThunk<
    P1Response<PatientDataAccessStatusDto>,
    AppointmentDetailsForPrescriptionDto,
    {state: RootState}
>(
    `${sliceName}/authorizePatientDataAccess`,
    async ({medic, patient, id}, thunkAPI) => {
        const {applicationId, authCode} =
            thunkAPI.getState().patientPrescriptionHistory;
        const {data} = await PatientDataAccessApi.authorizePatientDataAccess({
            appointmentId: id,
            patientId: patient.userId,
            medicId: medic.userId,
            authCode,
            medicNPWZ: medic.npwz,
            medicBusinessRole: medic.businessRole,
        });
        return data;
    },
);

export const applyForPatientDataAccess = createAsyncThunk<
    P1Response<PatientDataAccessStatusDto>,
    AppointmentDetailsForPrescriptionDto,
    {state: RootState}
>(
    `${sliceName}/applyForPatientDataAccess`,
    async ({medic, patient, id, child}, thunkAPI) => {
        console.log('applyForPatientDataAccess', {medic, patient, id, child});
        const {data} = await PatientDataAccessApi.applyForPatientDataAccess({
            appointmentId: id,
            patientId: patient.userId,
            medicId: medic.userId,
            patientPesel: child ? child.pesel : patient.pesel,
            medicNPWZ: medic.npwz,
            medicBusinessRole: medic.businessRole
        });
        return data;
    },
);

const authCodeSpecifiedReducer: CaseReducer<
    PrescriptionHistoryState,
    PayloadAction<string>
> = (state, action) => {
    state.authCode = action.payload;
};
const pageSpecifiedReducer: CaseReducer<
    PrescriptionHistoryState,
    PayloadAction<number>
> = (state, action) => {
    state.page = action.payload;
};

const sizeSpecifiedReducer: CaseReducer<
    PrescriptionHistoryState,
    PayloadAction<number>
> = (state, action) => {
    state.size = action.payload;
};

const fromDateSpecifiedReducer: CaseReducer<
    PrescriptionHistoryState,
    PayloadAction<Date | null>
> = (state, action) => {
    state.fromDateTime = action.payload;
};

const errorTextSpecifiedReducer: CaseReducer<
    PrescriptionHistoryState,
    PayloadAction<string>
> = (state, action) => {
    state.errorText = action.payload;
};

const patientPrescriptionHistory = createSlice({
    name: sliceName,
    initialState,
    reducers: {
        pageSpecified: pageSpecifiedReducer,
        sizeSpecified: sizeSpecifiedReducer,
        authCodeSpecified: authCodeSpecifiedReducer,
        fromDateSpecified: fromDateSpecifiedReducer,
        errorTextSpecified: errorTextSpecifiedReducer
    },
    extraReducers: builder => {
        builder
            .addCase(applyForPatientDataAccess.pending, state => {
                state.status = 'loading';
            })
            .addCase(applyForPatientDataAccess.fulfilled, (state, action) => {
                if (action.payload?.result) {
                    state.codeSent = true;
                    state.applicationDateTime =
                        action.payload?.result?.applicationDateTime ?? null;
                    state.authorizationDateTime =
                        action.payload?.result?.authorizationDateTime ?? null;
                }
                state.status = 'idle';
            })
            .addCase(applyForPatientDataAccess.rejected, state => {
                state.status = 'failed';
            })
            .addCase(authorizePatientDataAccess.pending, state => {
                state.status = 'loading';
            })
            .addCase(authorizePatientDataAccess.fulfilled, (state, action) => {
                if(action.payload?.result) {
                    state.applicationDateTime =
                        action.payload?.result?.applicationDateTime ?? null;
                    state.authorizationDateTime =
                        action.payload?.result?.authorizationDateTime ?? null;
                }
                state.status = 'idle';
            })
            .addCase(authorizePatientDataAccess.rejected, state => {
                state.status = 'failed';
            })
            .addCase(fetchPatientDataAccessStatus.pending, state => {
                state.status = 'loading';
            })
            .addCase(fetchPatientDataAccessStatus.fulfilled, (state, action) => {
                state.applicationDateTime =
                    action.payload?.result?.applicationDateTime ?? null;
                state.authorizationDateTime =
                    action.payload?.result?.authorizationDateTime ?? null;
                state.status = 'idle';
            })
            .addCase(fetchPatientDataAccessStatus.rejected, state => {
                state.status = 'failed';
            })
            .addCase(fetchPrescriptionHistory.pending, state => {
                state.status = 'loading';
            })
            .addCase(fetchPrescriptionHistory.fulfilled, (state, action) => {
                if (action.payload) {
                    state.prescriptionHistory = action.payload.results;
                    state.page = action.payload.page;
                    state.size = action.payload.size;
                    state.count = action.payload.count;
                }
                state.status = 'idle';
            })
            .addCase(fetchPrescriptionHistory.rejected, state => {
                state.status = 'failed';
            });
    },
});

export const {pageSpecified, sizeSpecified, authCodeSpecified, fromDateSpecified, errorTextSpecified} =
    patientPrescriptionHistory.actions;

export const selectPrescriptionHistory = (state: RootState) =>
    state.patientPrescriptionHistory.prescriptionHistory;

export const selectPage = (state: RootState) =>
    state.patientPrescriptionHistory.page;

export const selectSize = (state: RootState) =>
    state.patientPrescriptionHistory.size;

export const selectCount = (state: RootState) =>
    state.patientPrescriptionHistory.count;

export const selectStatus = (state: RootState) =>
    state.patientPrescriptionHistory.status;

export const selectCodeSent = (state: RootState) =>
    state.patientPrescriptionHistory.codeSent;

export const selectErrorText = (state: RootState) =>
    state.patientPrescriptionHistory.errorText;

export const selectFromDateTime = (state: RootState) =>
    state.patientPrescriptionHistory.fromDateTime;

export const selectApplicationDateTime = (state: RootState) =>
    state.patientPrescriptionHistory.applicationDateTime;

export const selectAuthorizationDateTime = (state: RootState) =>
    state.patientPrescriptionHistory.authorizationDateTime;

export default patientPrescriptionHistory.reducer;
